import React, { useMemo, useState, useEffect, useCallback } from 'react';
import styles from '../../styles/ShakeTemplateForm.module.scss';
import Accordion from './Accordion';
import FieldRenderer from './FieldRenderer';
import { useShake } from '../../ShakeProvider';
import { validateVersionName, validateFormats } from '../../utils/validation';
import CheckedIcon from '../../../icons/CheckedIcon';
import {
  getJobRawValue,
  buildOptionMediaValue,
  buildProjectMediaValue,
} from '../../utils/fieldMapping';

export default function TemplateForm() {
  const {
    selectedTemplate: tpl,
    selectedJob,
    view,
    goBack,
    createJob,
    createCopyFrom,
    saveDraft,
  } = useShake();

  const readOnly = view === 'job-view';

  // ---------- Basic state ----------
  const allFormats = tpl.formats || [];
  const initialFormats = new Set(selectedJob?.formats ?? []);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Only top-level errors
  const [nameError, setNameError] = useState(null);
  const [formatsError, setFormatsError] = useState(null);

  // Success view
  const [success, setSuccess] = useState(false);
  const [countdown, setCountdown] = useState(5);

  // Build form values from job or template defaults
  const templateFields = useMemo(() => tpl.fields || {}, [tpl]);

  const buildInitialValues = useCallback(() => {
    const base = {};

    for (const [cat, catFields] of Object.entries(templateFields)) {
      base[cat] = {};

      for (const [fname, spec] of Object.entries(catFields)) {
        const raw = getJobRawValue(selectedJob, cat, fname);
        let initialValue;

        if (['image', 'audio', 'video'].includes(spec.type)) {
          if (Array.isArray(spec.options) && spec.options.length > 0) {
            initialValue = buildOptionMediaValue(spec, raw);
          } else {
            // IMPORTANT: use raw consistently
            initialValue = buildProjectMediaValue(raw);
          }
        } else {
          if (typeof raw === 'string') initialValue = raw;
          else if (raw != null) initialValue = String(raw);
          else initialValue = spec.value ?? '';
        }

        base[cat][fname] = initialValue;
      }
    }

    return base;
  }, [templateFields, selectedJob]);

  const [values, setValues] = useState(() => buildInitialValues());
  const [formats, setFormats] = useState(() => new Set(selectedJob?.formats ?? []));
  const [versionName, setVersionName] = useState(() => selectedJob?.name || '');
  // re-hydrate when selection changes (open job, open draft, create new, create copy)
  useEffect(() => {
    setSubmitting(false);
    setError(null);
    setNameError(null);
    setFormatsError(null);
    setSuccess(false);

    setVersionName(selectedJob?.name || '');
    setFormats(new Set(selectedJob?.formats ?? []));
    setValues(buildInitialValues());
  }, [selectedJob, buildInitialValues]);

  const setField = (cat, fname, val) => {
    setValues((prev) => ({ ...prev, [cat]: { ...prev[cat], [fname]: val } }));
  };

  const toggleFormat = (fmt) => {
    setFormats((prev) => {
      const next = new Set(prev);
      next.has(fmt) ? next.delete(fmt) : next.add(fmt);
      return next;
    });
    // Clear formats error when user interacts
    setFormatsError(null);
  };

  const buildNormalizedFields = () => {
    const normalized = {};
    for (const [cat, catFields] of Object.entries(templateFields)) {
      normalized[cat] = {};
      for (const fname of Object.keys(catFields)) {
        const v = values?.[cat]?.[fname] ?? '';
        normalized[cat][fname] = {
          value: typeof v === 'object' ? JSON.stringify(v) : String(v),
          type: catFields[fname].type,
        };
      }
    }
    return normalized;
  };

  // ---------- Validation (only name + formats) ----------
  const validateAll = () => {
    const vErr = validateVersionName(versionName);
    const fErr = validateFormats(Array.from(formats));

    setNameError(vErr);
    setFormatsError(fErr);

    return !vErr && !fErr;
  };

  // ---------- Success countdown ----------
  useEffect(() => {
    if (!success) return;

    setCountdown(5);
    const id = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(id);
          goBack();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(id);
  }, [success, goBack]);

  const onSubmit = async (e) => {
    e.preventDefault();

    setError(null);
    if (!validateAll()) return;
    setSubmitting(true);

    const normalized = {};
    for (const [cat, catFields] of Object.entries(templateFields)) {
      normalized[cat] = {};
      for (const fname of Object.keys(catFields)) {
        const v = values?.[cat]?.[fname] ?? '';
        normalized[cat][fname] = {
          set_value: v?.name || String(v),
          type: catFields[fname].type,
          option: !!catFields[fname].options,
          key: v?.key || undefined,
          id: v?.id || undefined,
        };
      }
    }

    const result = await createJob({
      name: versionName.trim(),
      formats: Array.from(formats),
      fields: normalized,
    });
    if (result.success) {
      setSuccess(true);
    } else {
      console.error('Error creating job:', result.error);
      setError(result.error || 'An error occurred while creating the job.');
      setSubmitting(false);
    }
  };

  const onSaveDraft = () => {
    if (readOnly) return;
    //const normalized = buildNormalizedFields();
    saveDraft({
      name: (versionName || 'Untitled draft').trim(),
      formats: Array.from(formats),
      fields: values,
    });
    goBack();
  };

  // ---------- Render success view ----------
  if (success) {
    return (
      <div className={styles.successWrap}>
        <div className={styles.successCard}>
          <h2 className={styles.successTitle}>The job has been moved to processing.</h2>
          <p className={styles.successSubtitle}>Follow its status on the template page.</p>
          <p className={styles.successHint}>Go back to the template page</p>
          <button type="button" className={styles.successButton} onClick={goBack}>
            back ({countdown})
          </button>
        </div>
      </div>
    );
  }

  // ---------- Render form ----------
  return (
    <form
      className={`${styles.form} ${readOnly ? styles.readOnly : ''}`}
      onSubmit={onSubmit}
      noValidate
    >
      {error && <div className={styles.errorBanner}>{error}</div>}

      {/* Version name */}
      <div className={styles.row}>
        <label className={styles.label} htmlFor="versionName">
          Version name
        </label>
        <div className={styles.fieldWrap}>
          <input
            id="versionName"
            className={`${styles.input} ${nameError ? styles.inputError : ''}`}
            type="text"
            value={versionName}
            placeholder="Version name"
            onChange={(e) => {
              setVersionName(e.target.value);
              if (nameError) setNameError(null);
            }}
            onBlur={() => setNameError(validateVersionName(versionName))}
            maxLength={120}
            disabled={readOnly}
          />
          {!readOnly && nameError && <div className={styles.error}>{nameError}</div>}
        </div>
      </div>

      {/* Formats */}
      <div className={styles.row}>
        <label className={styles.label}>Formats</label>
        <div className={styles.formats}>
          {allFormats.map((fmt) => {
            const checked = formats.has(fmt);
            return (
              <label
                key={fmt}
                className={`${styles.formatItem} ${checked ? styles.checked : ''} ${
                  readOnly ? styles.disabled : ''
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleFormat(fmt)}
                  disabled={readOnly}
                />
                <span className={styles.formatBox} aria-hidden>
                  <CheckedIcon height="15px" fill="#fff" />
                </span>
                <span className={styles.formatName}>{fmt}</span>
              </label>
            );
          })}
        </div>
        <div />
        {!readOnly && formatsError && <div className={styles.error}>{formatsError}</div>}
      </div>

      {/* Categories */}
      <div className={styles.cats}>
        {Object.entries(templateFields).map(([cat, catFields]) => (
          <Accordion key={cat} title={cat} defaultOpen>
            <ul className={styles.fieldList}>
              {Object.entries(catFields).map(([fname, spec]) => {
                const val = values?.[cat]?.[fname] ?? '';
                return (
                  <li
                    key={fname}
                    className={styles.fieldRow}
                    style={{
                      order: spec.index || 99,
                    }}
                  >
                    <label className={styles.fieldLabel}>{fname}</label>
                    <div className={styles.fieldSlot}>
                      <FieldRenderer
                        spec={spec}
                        value={val}
                        onChange={(v) => setField(cat, fname, v)}
                        disabled={readOnly}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          </Accordion>
        ))}
      </div>

      {/* Actions */}
      <div className={styles.actions}>
        {readOnly ? (
          <>
            <button
              type="button"
              className={` shake__btnGhost`}
              onClick={() => createCopyFrom(selectedJob)}
            >
              Create copy
            </button>
            <button type="button" className={` shake__btnGhost`} onClick={goBack}>
              Back
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className={` shake__btnGhost`}
              onClick={onSaveDraft}
              disabled={submitting}
              style={{ marginRight: 'auto' }}
            >
              Save draft
            </button>
            <button
              type="button"
              className={` shake__btnGhost`}
              onClick={goBack}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`${styles.btnPrimary} shake__btnPrimary`}
              disabled={submitting}
            >
              {submitting ? 'Submitting…' : 'Submit'}
            </button>
          </>
        )}
      </div>
    </form>
  );
}
